import { createFileRoute } from "@tanstack/react-router";
import assert from "assert";
import { DM_Masonry_BrickKing } from "common/materials/library/designMaterialPath";
import { aabb2New } from "geom/aabb2Types";
import { planeNewXZ } from "geom/plane";
import { defaultCameraSettings } from "gfx/CameraSettings";
import { planeToTHREEMatrix4, scaleBufferGeometryUVs } from "gfx/convert";
import { HighlightColor, HighlightDataTexture } from "gfx/HighlightDataTexture";
import { setSingleIdAttribute } from "gfx/meshIdAttribute";
import PerspectiveControls from "gfx/PerspectiveControls";
import { THREETextRenderer } from "gfx/THREETextRenderer";
import { activeTHREEViewerServices } from "gfx/THREEViewerServices";
import type { THREEViewerSystem } from "gfx/THREEViewerSystem";
import useTHREEViewerSystem from "lib/components/studio/useTHREEViewerSystem";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import * as THREE from "three";
import withAuthentication from "../../components/withAuthentication";

function TestViewer() {
  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const viewerSystem = useRef<THREEViewerSystem>(null);
  const selectedSet = useRef<Set<number>>(new Set());
  const preselectedSet = useRef<Set<number>>(new Set());
  const highlightDataTexture = useRef<HighlightDataTexture>(new HighlightDataTexture());
  const { renderID } = useTHREEViewerSystem({
    viewerSystem: viewerSystem,
    viewerContainerRef: viewerContainerRef,
    setLoading: () => {},
    highlightDataTexture: highlightDataTexture.current,
  });

  const cubeMesh = useRef<THREE.Object3D | undefined>(undefined);

  const [screenshotURL, setScreenshotURL] = useState("");
  const updateScreenshot = () => {
    if (!viewerSystem.current) {
      return;
    }
    const pixelWidth = 800;
    const pixelHeight = 600;
    const camera = new THREE.PerspectiveCamera(50, 800 / 600, 1, 10000);
    camera.position.set(100, -800, 300);
    camera.up.set(0, 0, 1);
    camera.lookAt(new THREE.Vector3(0, 0, 0));
    camera.updateMatrixWorld();
    const viewerRenderer = viewerSystem.current.viewer.viewerRenderer;
    assert(viewerRenderer.ssfxParamsManager);
    assert(viewerSystem.current.viewer.scene.ssfxRendererScene);
    const url = activeTHREEViewerServices.imageRenderer.renderImageSync(
      viewerSystem.current.viewer.scene.ssfxRendererScene,
      camera,
      viewerRenderer.ssfxParamsManager.getRenderParams(),
      {
        width: pixelWidth,
        height: pixelHeight,
      },
    );
    setScreenshotURL(url ?? "");
  };

  const loseContext = () => {
    viewerSystem.current?.viewer.viewerRenderer?.renderer.forceContextLoss();
  };

  function isSelected(num: number) {
    return selectedSet.current.has(num);
  }

  function isPreselected(num: number) {
    return preselectedSet.current.has(num);
  }

  useEffect(() => {
    const viewerSys = viewerSystem.current;
    if (viewerSys) {
      const viewer = viewerSys.viewer;
      viewer.scene.highlightDataTexture = highlightDataTexture.current;

      function mouseMove(ev: MouseEvent) {
        const pickedEntities = viewer.pickEntityIds(aabb2New(ev.clientX, ev.clientY, ev.clientX + 1, ev.clientY + 1));
        const newlyPreselected = pickedEntities.filter((id) => !isPreselected(id));
        for (const id of newlyPreselected) {
          highlightDataTexture.current.preselectId(id, HighlightColor.BLUE);
        }
        const depreselected = [...preselectedSet.current].filter((id) => pickedEntities.indexOf(id) < 0);
        for (const id of depreselected) {
          highlightDataTexture.current.depreselectId(id, HighlightColor.BLUE);
        }
        if (newlyPreselected.length > 0 || depreselected.length > 0) {
          for (const id of newlyPreselected) {
            preselectedSet.current.add(id);
          }
          for (const id of depreselected) {
            preselectedSet.current.delete(id);
          }
          viewer.scene.markRenderingNeedsUpdate();
        }
      }
      function mouseDown(ev: MouseEvent) {
        const pickedEntities = viewer.pickEntityIds(aabb2New(ev.clientX, ev.clientY, ev.clientX + 1, ev.clientY + 1));
        const newlySelected = pickedEntities.filter((id) => !isSelected(id));
        for (const id of newlySelected) {
          highlightDataTexture.current.selectId(id, HighlightColor.BLUE);
        }
        const deselected = [...selectedSet.current].filter((id) => pickedEntities.indexOf(id) < 0);
        for (const id of deselected) {
          highlightDataTexture.current.deselectId(id, HighlightColor.BLUE);
        }
        if (newlySelected.length > 0 || deselected.length > 0) {
          for (const id of newlySelected) {
            selectedSet.current.add(id);
          }
          for (const id of deselected) {
            selectedSet.current.delete(id);
          }
          viewer.scene.markRenderingNeedsUpdate();
        }
      }
      viewer.container?.addEventListener("mousemove", mouseMove);
      viewer.container?.addEventListener("mousedown", mouseDown);

      viewer.setSceneBackground(new THREE.Color("#736968"));
      viewer.skyboxVisible = false;

      void (async () => {
        const brickMaterial = await viewerSys.materialFactory.getDesignMaterial(
          DM_Masonry_BrickKing.L.Brick_Acme.Brick_Acme_AdobeWells,
        );
        const geometry = new THREE.BoxGeometry(500, 500, 500);
        scaleBufferGeometryUVs(geometry, 20);
        setSingleIdAttribute(geometry, 1);
        viewer.scene.clear();
        cubeMesh.current = new THREE.Mesh(geometry, brickMaterial ?? undefined);
        viewer.scene.add(cubeMesh.current);

        const textTransform = planeToTHREEMatrix4(planeNewXZ());
        const [, textMesh] = THREETextRenderer.instance.renderText(viewer.scene, {
          text: "The quick brown fox jumps over the lazy dog",
          x: 0,
          y: 0,
          z: 400,
          transform: textTransform,
        });
        viewer.scene.add(textMesh);
      })();

      viewer.setCameraSettings({
        ...defaultCameraSettings,
        targetX: 0,
        targetY: 0,
        targetZ: 0,
      });
      const cameraControls = new PerspectiveControls(viewer.camera, viewer.container, 0, 0, 0);
      viewer.setControls(cameraControls);

      let frameCallback: number;

      function frame() {
        // Rotate for 1 second, then stop for 1 second.
        // Should see AA resolve to a nice state while the rotation is stopped.
        if (Math.floor(Date.now() / 1000) % 2 === 0) {
          if (cubeMesh.current) {
            cubeMesh.current.rotateZ(0.05);
            cubeMesh.current.updateMatrix();
            cubeMesh.current.updateMatrixWorld();
            if (viewerSystem.current) {
              viewerSystem.current.viewer.markRendererForUpdate();
            }
          }
        }
        frameCallback = window.requestAnimationFrame(frame);
      }

      frameCallback = window.requestAnimationFrame(frame);

      return () => {
        viewer.container?.removeEventListener("mousemove", mouseMove);
        viewer.container?.removeEventListener("mousedown", mouseDown);
        window.cancelAnimationFrame(frameCallback);
        viewerSys.viewer.scene.clear();
      };
    }
    return undefined;
  }, [viewerSystem, renderID]);

  return (
    <div style={{ height: "100%" }}>
      <div ref={viewerContainerRef} id="viewerB" style={{ width: "100%", height: "100%", position: "relative" }}></div>
      <div style={{ position: "absolute", top: 10, right: 10, width: 200, height: 150, background: "grey" }}>
        {screenshotURL && <img style={{ width: "100%" }} src={screenshotURL} />}
        <button style={{ position: "absolute", bottom: 0, left: 0 }} onClick={updateScreenshot}>
          Update screenshot
        </button>
      </div>
      <div style={{ position: "absolute", top: 170, right: 10, display: "flex", flexDirection: "column" }}>
        <button onClick={loseContext}>Lose WebGL context</button>
        <label>
          <input
            type="checkbox"
            onChange={(ev) => viewerSystem.current?.viewer.setDebugFeaturesEnabled(ev.target.checked)}
          />
          Debug features enabled
        </label>
      </div>
    </div>
  );
}

function WebGLViewerStatePage() {
  return (
    <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
      <Head>
        <title>{getPageTitle("WebGL Viewer State Test")}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <TestViewer />
    </div>
  );
}

export const Route = createFileRoute("/debug/viewer-state-management")({
  component: withAuthentication(WebGLViewerStatePage, { superAdmin: true }),
});
